import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Calendar as CalendarIcon, Plus, ChevronLeft, ChevronRight, X, RefreshCw, Clock } from 'lucide-react';
import api from '../api';
import { toast } from 'sonner';
import { RecertVisitActions } from '../components/RecertVisitActions';
import { NurseWorkload } from '../components/NurseWorkload';

const SchedulingPage = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    patientId: '',
    patientName: '',
    clinician: '',
    date: new Date().toISOString().split('T')[0],
    time: '09:00 AM',
    type: 'Skilled Nursing'
  });

  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const timeSlots = [
    '08:00 AM', '09:00 AM', '10:00 AM', '11:00 AM',
    '12:00 PM', '01:00 PM', '02:00 PM', '03:00 PM', '04:00 PM'
  ];

  const visitTypes = [
    'Skilled Nursing',
    'Physical Therapy',
    'Occupational Therapy',
    'Speech Therapy',
    'Home Health Aide',
    'Medical Social Worker',
    'Wound Care',
    'IV Therapy',
    'Assessment',
    'Recertification',
    'Discharge Visit'
  ];
  const [clinicianFilter, setClinicianFilter] = useState('');
  const clinicians = [...new Set(appointments.map(a => a.clinician).filter(Boolean))].sort();
  const shown = clinicianFilter ? appointments.filter(a => a.clinician === clinicianFilter) : appointments;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [appointmentsRes, patientsRes] = await Promise.all([
        api.get('/appointments'),
        api.get('/patients')
      ]);

      if (appointmentsRes.data.success) {
        setAppointments(appointmentsRes.data.appointments);
      }
      if (patientsRes.data.success) {
        setPatients(patientsRes.data.patients);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAppointment = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/appointments', formData);
      if (response.data.success) {
        toast.success('Appointment scheduled successfully');
        setShowAddModal(false);
        resetForm();
        fetchData();
      }
    } catch (error) {
      console.error('Error creating appointment:', error);
      toast.error('Failed to schedule appointment');
    }
  };

  const handlePatientSelect = (patientId) => {
    const patient = patients.find(p => p.id === patientId);
    if (patient) {
      setFormData({
        ...formData,
        patientId: patient.id,
        patientName: `${patient.firstName} ${patient.lastName}`
      });
    }
  };

  const resetForm = () => {
    setFormData({
      patientId: '',
      patientName: '',
      clinician: '',
      date: new Date().toISOString().split('T')[0],
      time: '09:00 AM',
      type: 'Skilled Nursing'
    });
  };

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-800';
      case 'Scheduled': return 'bg-blue-100 text-blue-800';
      case 'Cancelled': return 'bg-red-100 text-red-800';
      case 'In Progress': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="scheduling-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Scheduling</h1>
            <p className="text-gray-600 mt-1">Manage appointments and visits</p>
          </div>
          <button 
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center px-4 py-2 bg-[#ff6634] text-white rounded-lg hover:bg-[#e55a2d] transition-colors"
            data-testid="schedule-visit-button"
          >
            <Plus className="w-5 h-5 mr-2" />
            Schedule Visit
          </button>
        </div>

        {/* Calendar Controls */}
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <button onClick={prevMonth} className="p-2 hover:bg-gray-100 rounded-lg">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center space-x-2">
              <CalendarIcon className="w-5 h-5 text-[#ea580c]" />
              <span className="font-semibold text-lg">
                {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={nextMonth} className="p-2 hover:bg-gray-100 rounded-lg">
                <ChevronRight className="w-5 h-5" />
              </button>
              <button onClick={fetchData} className="p-2 hover:bg-gray-100 rounded-lg" title="Refresh">
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Appointments List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <h2 className="text-xl font-bold text-gray-800">Upcoming Appointments</h2>
              <select value={clinicianFilter} onChange={(e) => setClinicianFilter(e.target.value)} data-testid="schedule-clinician-filter"
                className="px-3 py-2 border rounded-lg text-sm">
                <option value="">All clinicians</option>
                {clinicians.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="p-6">
            {loading ? (
              <div className="text-center py-8">
                <RefreshCw className="w-8 h-8 animate-spin text-[#ea580c] mx-auto mb-4" />
                <p className="text-gray-500">Loading appointments...</p>
              </div>
            ) : shown.length === 0 ? (
              <div className="text-center py-8">
                <CalendarIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No appointments scheduled</h3>
                <p className="text-gray-500 mb-4">Get started by scheduling your first visit</p>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="inline-flex items-center px-4 py-2 bg-[#ff6634] text-white rounded-lg hover:bg-[#e55a2d]"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Schedule Visit
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {shown.map((appointment) => (
                  <div key={appointment.id} className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow" data-testid={`appointment-${appointment.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                            {appointment.type}
                          </span>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(appointment.status)}`}>
                            {appointment.status}
                          </span>
                        </div>
                        <h3 className="font-semibold text-lg text-gray-800 mb-1">{appointment.patientName}</h3>
                        <p className="text-sm text-gray-600 mb-1">Clinician: {appointment.clinician}</p>
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <span className="flex items-center">
                            <CalendarIcon className="w-4 h-4 mr-1" />
                            {new Date(appointment.date).toLocaleDateString()}
                          </span>
                          <span className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            {appointment.time}
                          </span>
                        </div>
                        {appointment.type === 'Recertification' && appointment.recertPeriodEnd && (
                          <RecertVisitActions appointment={appointment} onChanged={fetchData} />
                        )}
                      </div>
                      {appointment.type !== 'Recertification' && (
                        <button className="px-4 py-2 text-[#ea580c] border border-[#ea580c] rounded-lg hover:bg-[#ea580c] hover:text-white transition-colors">
                          View Details
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <NurseWorkload />

        {/* Weekly View */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-800">Week View</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                  {days.map((day) => (
                    <th key={day} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      {day}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {timeSlots.map((time) => (
                  <tr key={time}>
                    <td className="px-4 py-3 text-sm text-gray-600 font-medium">{time}</td>
                    {days.map((day) => (
                      <td key={`${day}-${time}`} className="px-4 py-3 text-center">
                        <div 
                          className="h-12 hover:bg-[#ea580c]/10 rounded cursor-pointer border border-transparent hover:border-[#ea580c]/30 transition-colors"
                          onClick={() => {
                            setFormData({...formData, time});
                            setShowAddModal(true);
                          }}
                        ></div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Add Appointment Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-800">Schedule New Visit</h2>
                <button 
                  onClick={() => { setShowAddModal(false); resetForm(); }}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleAddAppointment} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Patient</label>
                  <select
                    required
                    value={formData.patientId}
                    onChange={(e) => handlePatientSelect(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ea580c] focus:border-transparent"
                  >
                    <option value="">Select a patient</option>
                    {patients.map((patient) => (
                      <option key={patient.id} value={patient.id}>
                        {patient.firstName} {patient.lastName}
                      </option>
                    ))}
                  </select>
                  {patients.length === 0 && (
                    <p className="text-sm text-orange-600 mt-1">No patients found. Add a patient first.</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Visit Type</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ea580c] focus:border-transparent"
                  >
                    {visitTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Clinician</label>
                  <input
                    type="text"
                    required
                    value={formData.clinician}
                    onChange={(e) => setFormData({...formData, clinician: e.target.value})}
                    placeholder="e.g., Nurse Jennifer Davis"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ea580c] focus:border-transparent"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                    <input
                      type="date"
                      required
                      value={formData.date}
                      onChange={(e) => setFormData({...formData, date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ea580c] focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                    <select
                      value={formData.time}
                      onChange={(e) => setFormData({...formData, time: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ea580c] focus:border-transparent"
                    >
                      {timeSlots.map((time) => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => { setShowAddModal(false); resetForm(); }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={patients.length === 0}
                    className="flex-1 px-4 py-2 bg-[#ff6634] text-white rounded-lg hover:bg-[#e55a2d] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Schedule Visit
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SchedulingPage;
